/**
 * KL24 cart UX — shipping gates sync, discount codes, checkout state, milestone toasts.
 */
(function () {
  const CART_MIN_ORDER_CENTS = Number(window.kl24ShippingRules?.minOrderCents) || 500;
  const CART_COUNTRY = String(window.kl24ShippingRules?.countryIso || 'DE').toUpperCase();
  const CART_FREE_SHIPPING_CENTS =
    CART_COUNTRY === 'AT'
      ? Number(window.kl24ShippingRules?.freeShippingFromCentsAt) || 5900
      : Number(window.kl24ShippingRules?.freeShippingFromCentsDe) ||
        Number(window.kl24ShippingRules?.freeShippingFromCents) ||
        3900;
  const CART_FREE_SHIP_LABEL = CART_COUNTRY === 'AT' ? '59 €' : '39 €';
  const VOUCHER_MAX_SHARE = 0.75;
  const MILESTONES_STORAGE_KEY = 'kl24_cart_milestones_shown';
  const TOAST_VISIBLE_MS = 3800;

  /** @type {number|null} */
  let prevSubtotalCents = null;
  let milestonesShown = { min: false, free: false };

  try {
    const saved = JSON.parse(sessionStorage.getItem(MILESTONES_STORAGE_KEY) || '{}');
    milestonesShown.min = !!saved.min;
    milestonesShown.free = !!saved.free;
  } catch {
    milestonesShown = { min: false, free: false };
  }

  /** Always "0,00 €" — delegates to central formatMoneyDE. */
  const formatEuro = (cents) => {
    const fmt = window.formatMoneyDE || window.Kl24EuroFormat?.formatMoneyDE;
    if (fmt) return fmt(cents);
    const value = Math.max(0, Number(cents) || 0) / 100;
    const amount = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `${amount}\u00A0€`;
  };

  const formatEuroScale = (cents) => formatEuro(cents);

  const layoutProgressScale = (panelEl) => {
    const scale = panelEl.querySelector('.cart-progress-panel__scale');
    const currentEl = panelEl.querySelector('[data-cart-progress-current]');
    const minLabel = panelEl.querySelector('[data-cart-progress-min], .cart-progress-panel__marker-label--min');
    const goalEl = panelEl.querySelector('[data-cart-progress-goal]');
    if (!scale || !currentEl || !minLabel || !goalEl) return;

    currentEl.classList.remove('is-crowded');
    currentEl.hidden = false;

    const gap = 10;
    const currentRect = currentEl.getBoundingClientRect();
    const minRect = minLabel.getBoundingClientRect();
    const goalRect = goalEl.getBoundingClientRect();

    if (currentRect.width > 0 && minRect.left - currentRect.right < gap) {
      currentEl.classList.add('is-crowded');
      currentEl.hidden = true;
    }

    // If 5 € label collides with 39 €, nudge it left via max clamp (CSS) — nothing else needed.
    if (minRect.right + gap > goalRect.left) {
      minLabel.style.transform = 'translateX(-100%)';
    } else {
      minLabel.style.transform = 'translateX(-50%)';
    }
  };

  const strings = () => window.kl24CartStrings || {};

  const calcFreeShippingProgress = (subtotal) =>
    Math.min(100, subtotal >= CART_FREE_SHIPPING_CENTS ? 100 : Math.floor((subtotal * 100) / CART_FREE_SHIPPING_CENTS));

  const persistMilestonesShown = () => {
    try {
      sessionStorage.setItem(MILESTONES_STORAGE_KEY, JSON.stringify(milestonesShown));
    } catch {
      /* ignore */
    }
  };

  const resetMilestoneTracking = () => {
    prevSubtotalCents = 0;
    milestonesShown = { min: false, free: false };
    persistMilestonesShown();
  };

  const detectMilestoneCrossings = (subtotal, itemCount) => {
    /** @type {Array<'min'|'free'>} */
    const events = [];

    if (itemCount === 0) {
      resetMilestoneTracking();
      return events;
    }

    if (prevSubtotalCents !== null) {
      if (
        prevSubtotalCents < CART_MIN_ORDER_CENTS &&
        subtotal >= CART_MIN_ORDER_CENTS &&
        !milestonesShown.min
      ) {
        events.push('min');
        milestonesShown.min = true;
      }
      if (
        prevSubtotalCents < CART_FREE_SHIPPING_CENTS &&
        subtotal >= CART_FREE_SHIPPING_CENTS &&
        !milestonesShown.free
      ) {
        events.push('free');
        milestonesShown.free = true;
      }
      if (events.length) persistMilestonesShown();
    }

    prevSubtotalCents = subtotal;
    return events;
  };

  const buildProgressStatus = (subtotal, belowMinimum, freeShippingUnlocked, minimumUnlocked) => {
    const s = strings();
    const minRemaining = Math.max(0, CART_MIN_ORDER_CENTS - subtotal);
    const freeRemaining = Math.max(0, CART_FREE_SHIPPING_CENTS - subtotal);

    if (freeShippingUnlocked) {
      return s.freeShippingUnlocked || 'Versandkostenfrei freigeschaltet — Versandkosten sparen!';
    }
    if (belowMinimum) {
      const tpl =
        s.progressStatusMinimum ||
        s.progressStatusBoth ||
        'Noch {{ amount }} bis zum Mindestbestellwert (5 €)';
      return tpl
        .replace('{{ amount }}', formatEuro(minRemaining))
        .replace('{{ min_amount }}', formatEuro(minRemaining))
        .replace('{{ ship_amount }}', formatEuro(freeRemaining));
    }
    if (minimumUnlocked) {
      const tpl =
        s.minimumOrderUnlockedStatus ||
        `Noch {{ amount }} bis versandkostenfrei (${CART_FREE_SHIP_LABEL})`;
      return tpl
        .replace('{{ amount }}', formatEuro(freeRemaining))
        .replace(/\(39\s*€\)/g, `(${CART_FREE_SHIP_LABEL})`)
        .replace(/\(59\s*€\)/g, `(${CART_FREE_SHIP_LABEL})`);
    }
    const tpl =
      s.freeShippingRemaining ||
      `Noch {{ amount }} bis versandkostenfrei (${CART_FREE_SHIP_LABEL})`;
    return tpl
      .replace('{{ amount }}', formatEuro(freeRemaining))
      .replace(/\(39\s*€\)/g, `(${CART_FREE_SHIP_LABEL})`)
      .replace(/\(59\s*€\)/g, `(${CART_FREE_SHIP_LABEL})`);
  };

  const milestoneToastMessage = (kind) => {
    const s = strings();
    if (kind === 'min') {
      return s.minimumOrderUnlockedToast || 'Mindestbestellwert erreicht — zur Kasse geht\'s!';
    }
    return s.freeShippingUnlockedToast || 'Gratis-Versand ist dabei!';
  };

  const ensureMilestoneToast = (panelEl) => {
    const inner = panelEl.querySelector('.cart-progress-panel__inner');
    if (!inner) return null;
    let toast = inner.querySelector('[data-cart-milestone-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cart-progress-panel__toast';
      toast.dataset.cartMilestoneToast = '';
      toast.hidden = true;
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      inner.insertBefore(toast, inner.firstChild);
    }
    return toast;
  };

  /** @type {Map<HTMLElement, number>} */
  const toastTimers = new Map();

  const showMilestoneToast = (panelEl, kind) => {
    const toast = ensureMilestoneToast(panelEl);
    if (!toast) return;

    const existing = toastTimers.get(panelEl);
    if (existing) window.clearTimeout(existing);

    toast.textContent = milestoneToastMessage(kind);
    toast.hidden = false;
    toast.classList.remove('cart-progress-panel__toast--min', 'cart-progress-panel__toast--free');
    toast.classList.add(kind === 'min' ? 'cart-progress-panel__toast--min' : 'cart-progress-panel__toast--free');
    toast.classList.add('cart-progress-panel__toast--visible');

    const timer = window.setTimeout(() => {
      toast.classList.remove('cart-progress-panel__toast--visible');
      window.setTimeout(() => {
        toast.hidden = true;
      }, 280);
      toastTimers.delete(panelEl);
    }, TOAST_VISIBLE_MS);
    toastTimers.set(panelEl, timer);
  };

  const syncCartShippingGates = (cart) => {
    if (!cart || typeof cart !== 'object') return;

    const subtotal = Number(cart.items_subtotal_price) || 0;
    const itemCount = Number(cart.item_count) || 0;
    const belowMinimum = itemCount > 0 && subtotal < CART_MIN_ORDER_CENTS;
    const minimumUnlocked = itemCount > 0 && subtotal >= CART_MIN_ORDER_CENTS;
    const freeShippingUnlocked = subtotal >= CART_FREE_SHIPPING_CENTS;
    const progress = calcFreeShippingProgress(subtotal);
    const freeRemaining = Math.max(0, CART_FREE_SHIPPING_CENTS - subtotal);

    const milestoneEvents = detectMilestoneCrossings(subtotal, itemCount);
    let toastShown = false;

    document.querySelectorAll('.cart-shipping-gates, .cart-progress-panel').forEach((el) => {
      el.dataset.cartSubtotalCents = String(subtotal);
      el.dataset.cartBelowMinimum = belowMinimum ? 'true' : 'false';
      el.dataset.minimumUnlocked = minimumUnlocked && !freeShippingUnlocked ? 'true' : 'false';
      el.dataset.freeShippingUnlocked = freeShippingUnlocked ? 'true' : 'false';
      el.hidden = itemCount === 0;

      if (!toastShown && milestoneEvents.length) {
        const kind = milestoneEvents.includes('free') ? 'free' : 'min';
        showMilestoneToast(el, kind);
        toastShown = true;
      }

      const statusEl = el.querySelector('[data-cart-progress-status]');
      if (statusEl) {
        statusEl.textContent = buildProgressStatus(
          subtotal,
          belowMinimum,
          freeShippingUnlocked,
          minimumUnlocked && !freeShippingUnlocked
        );
        statusEl.classList.toggle('cart-progress-panel__status--unlocked', freeShippingUnlocked);
        statusEl.classList.toggle(
          'cart-progress-panel__status--minimum-unlocked',
          minimumUnlocked && !freeShippingUnlocked
        );
        statusEl.toggleAttribute('data-status-unlocked', freeShippingUnlocked);
        statusEl.toggleAttribute(
          'data-status-minimum-unlocked',
          minimumUnlocked && !freeShippingUnlocked
        );
      }

      const currentEl = el.querySelector('[data-cart-progress-current]');
      if (currentEl) currentEl.textContent = formatEuroScale(subtotal);

      const goalEl = el.querySelector('[data-cart-progress-goal]');
      if (goalEl) goalEl.textContent = formatEuroScale(CART_FREE_SHIPPING_CENTS);

      const minMarker = el.querySelector('.cart-progress-panel__marker--min');
      if (minMarker) minMarker.classList.toggle('is-unlocked', minimumUnlocked);

      const freeMarker = el.querySelector('.cart-progress-panel__marker--free');
      if (freeMarker) freeMarker.classList.toggle('is-unlocked', freeShippingUnlocked);

      const minLabel = el.querySelector('[data-cart-progress-min], .cart-progress-panel__marker-label--min');
      if (minLabel) {
        minLabel.textContent = formatEuroScale(CART_MIN_ORDER_CENTS);
        minLabel.classList.toggle('is-unlocked', minimumUnlocked);
      }

      const scale = el.querySelector('.cart-progress-panel__scale');
      if (scale) {
        const minPct = Number(el.dataset.minMarkerPct) || Math.round((CART_MIN_ORDER_CENTS * 100) / CART_FREE_SHIPPING_CENTS);
        scale.style.setProperty('--kl24-min-marker-pct', `${minPct}%`);
      }

      const fill = el.querySelector('[data-free-shipping-progress]');
      const track = el.querySelector('.cart-progress-panel__track');
      if (fill) {
        fill.style.width = `${progress}%`;
        fill.dataset.freeShippingProgress = String(progress);
        fill.classList.toggle('cart-progress-panel__fill--complete', freeShippingUnlocked);
        fill.classList.toggle('cart-progress-panel__fill--pulse', freeShippingUnlocked);
      }
      if (track) {
        track.setAttribute('aria-valuenow', String(progress));
        track.classList.toggle('cart-progress-panel__track--free-unlocked', freeShippingUnlocked);
      }

      layoutProgressScale(el);
    });

    // Re-measure after layout/fonts settle (narrow summary card).
    window.requestAnimationFrame(() => {
      document.querySelectorAll('.cart-shipping-gates, .cart-progress-panel').forEach(layoutProgressScale);
    });

    const fillZiel =
      subtotal < CART_MIN_ORDER_CENTS ? CART_MIN_ORDER_CENTS / 100 : CART_FREE_SHIPPING_CENTS / 100;
    const showFillRest = itemCount > 0 && !freeShippingUnlocked;
    document.querySelectorAll('[data-cart-fill-rest]').forEach((link) => {
      link.hidden = !showFillRest;
      if (showFillRest) link.href = `/pages/auffuellen?ziel=${fillZiel}`;
    });

    document.querySelectorAll('[data-cart-shipping-note-row]').forEach((row) => {
      row.hidden = freeShippingUnlocked;
    });

    const gapHint = document.querySelector('[data-cross-sell-gap-hint]');
    if (gapHint && !freeShippingUnlocked && itemCount > 0) {
      const tpl = strings().crossSellGapDynamic || 'Noch {{ amount }} bis Gratis-Versand';
      gapHint.textContent = tpl.replace('{{ amount }}', formatEuro(freeRemaining));
    } else if (gapHint) {
      gapHint.textContent = strings().crossSellGapHint || 'Set vervollständigen';
    }
  };

  const syncCheckoutMinimumHints = (subtotal, itemCount) => {
    const belowMinimum = itemCount > 0 && subtotal < CART_MIN_ORDER_CENTS;
    const minRemaining = Math.max(0, CART_MIN_ORDER_CENTS - subtotal);
    const s = strings();
    const checkoutTpl =
      s.checkoutMinimumHint || 'Noch {{ amount }} bis zur Mindestbestellung';
    const checkoutText = belowMinimum
      ? checkoutTpl.replace('{{ amount }}', formatEuro(minRemaining))
      : '';

    document
      .querySelectorAll('[data-cart-checkout-minimum-hint], [data-kl24-cart-checkout-hint]')
      .forEach((el) => {
        if (belowMinimum) {
          el.hidden = false;
          el.textContent = checkoutText;
        } else {
          el.hidden = true;
          el.textContent = '';
        }
      });
  };

  const syncCheckoutButtons = (cart) => {
    if (!cart || typeof cart !== 'object') return;

    const subtotal = Number(cart.items_subtotal_price) || 0;
    const itemCount = Number(cart.item_count) || 0;
    const belowMinimum = itemCount > 0 && subtotal < CART_MIN_ORDER_CENTS;
    const disabled = itemCount === 0 || belowMinimum;
    const minRemaining = Math.max(0, CART_MIN_ORDER_CENTS - subtotal);
    const s = strings();

    document.querySelectorAll('[data-cart-checkout-button], [data-kl24-cart-checkout]').forEach((button) => {
      const isPeekCheckout = button.hasAttribute('data-kl24-cart-checkout');
      if (!isPeekCheckout) {
        button.disabled = disabled;
      }
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      button.classList.toggle('is-disabled', disabled);
      button.classList.toggle('cart__checkout-button--need-more', belowMinimum);
      if (belowMinimum) {
        const tpl =
          s.checkoutButtonMinimum ||
          s.checkoutAriaMinimum ||
          'Noch {{ amount }} bis Mindestbestellwert (5 €)';
        const label = tpl.replace('{{ amount }}', formatEuro(minRemaining));
        button.setAttribute('aria-label', label);
        button.textContent = label;
      } else {
        button.removeAttribute('aria-label');
        if (isPeekCheckout) {
          button.textContent = 'Jetzt zur Kasse';
        } else if (button.id === 'CartDrawer-Checkout' || button.hasAttribute('data-cart-checkout-button')) {
          const secure = button.dataset.defaultLabel;
          if (secure) button.textContent = secure;
        }
      }
    });

    document.querySelectorAll('[data-cart-summary-subtotal]').forEach((el) => {
      el.textContent = formatEuro(subtotal);
    });

    document.querySelectorAll('[data-cart-summary-total]').forEach((el) => {
      el.textContent = formatEuro(Number(cart.total_price) || 0);
    });

    const discountCents = Number(cart.total_discount) || 0;
    document.querySelectorAll('[data-cart-summary-discount-row]').forEach((row) => {
      row.hidden = discountCents <= 0;
      const val = row.querySelector('[data-cart-summary-discount]');
      if (val) val.textContent = `−${formatEuro(discountCents)}`;
    });

    syncCheckoutMinimumHints(subtotal, itemCount);

    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((el) => {
      el.hidden = belowMinimum;
    });
  };

  const syncCartState = (cart) => {
    syncCartShippingGates(cart);
    syncCheckoutButtons(cart);
    if (window.Kl24CartDrawerCheckout?.syncAll) {
      window.Kl24CartDrawerCheckout.syncAll(cart);
    }
  };

  const applyDiscountCode = (code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) return Promise.reject(new Error(strings().discountEmpty || 'Bitte Code eingeben.'));

    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    return fetch(`/discount/${encodeURIComponent(trimmed)}?redirect=${redirect}`, {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual',
    }).then((response) => {
      if (response.type === 'opaqueredirect' || response.status === 0 || response.ok) {
        window.location.reload();
        return;
      }
      throw new Error(strings().discountInvalid || 'Code ungültig oder abgelaufen.');
    });
  };

  const bindDiscountForms = () => {
    document.querySelectorAll('[data-kl24-discount-form]').forEach((form) => {
      if (form.dataset.kl24DiscountBound === '1') return;
      form.dataset.kl24DiscountBound = '1';

      const input = form.querySelector('[data-kl24-discount-input]');
      const errorEl = form.querySelector('[data-kl24-discount-error]');
      const submitBtn = form.querySelector('[data-kl24-discount-submit]');

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!input) return;

        if (errorEl) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
        if (submitBtn) submitBtn.disabled = true;

        applyDiscountCode(input.value).catch((error) => {
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = error.message || strings().discountInvalid;
          }
          if (submitBtn) submitBtn.disabled = false;
        });
      });
    });
  };

  const fetchCartAndSync = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (cart) syncCartState(cart);
        return cart;
      })
      .catch(() => null);

  const clearCartLoadingState = () => {
    document.querySelectorAll('.template-cart .loading__spinner').forEach((el) => {
      el.classList.add('hidden');
    });
    document.querySelectorAll('.template-cart .cart__items--disabled').forEach((el) => {
      el.classList.remove('cart__items--disabled');
    });
    document.querySelectorAll('.template-cart .kl24-cart-summary-card.is-loading, .template-cart .cart-enhancements.is-loading').forEach((el) => {
      el.classList.remove('is-loading');
    });
    const lineStatus =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');
    if (lineStatus) lineStatus.setAttribute('aria-hidden', 'true');
  };

  const init = () => {
    clearCartLoadingState();
    bindDiscountForms();
    fetchCartAndSync().finally(clearCartLoadingState);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('cart:refresh', () => {
    fetchCartAndSync().finally(clearCartLoadingState);
  });

  document.addEventListener('cart:updated', (event) => {
    clearCartLoadingState();
    if (event.detail?.cart && window.Kl24CartMinFill?.syncFromCart) {
      window.Kl24CartMinFill.syncFromCart(event.detail.cart);
    }
  });
  document.addEventListener('pubsub:cart-update', clearCartLoadingState);

  window.Kl24CartEnhancements = {
    CART_MIN_ORDER_CENTS,
    CART_FREE_SHIPPING_CENTS,
    VOUCHER_MAX_SHARE,
    formatEuro,
    syncCartState,
    syncCartShippingGates,
    syncCheckoutButtons,
    applyDiscountCode,
    fetchCartAndSync,
    calcVoucherRedeemable: (subtotalCents, balanceCents) =>
      Math.min(Math.max(0, Number(balanceCents) || 0), Math.floor((Number(subtotalCents) || 0) * VOUCHER_MAX_SHARE)),
  };

  window.syncCartShippingCheckoutState = syncCartState;
})();
